import { type ComponentType, type LazyExoticComponent, lazy } from 'react';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import { isChunkLoadError } from './deploy-watcher';

export const STALE_PAGE_TITLE = 'This page was updated.';

/**
 * Stands in for a page whose chunk the deploy removed. It reloads only when
 * the user presses Reload: an open tab must never reload under a sidebar
 * click.
 */
export function StalePage({ reload = () => window.location.reload() }) {
  return (
    <EmptyState
      title={STALE_PAGE_TITLE}
      description="A new version of Skupreme is out. Reload to open this page."
      action={<Button onPress={reload}>Reload</Button>}
    />
  );
}

export type LazyPage<T extends ComponentType<any>> = LazyExoticComponent<T> & {
  /** Fetches the page's chunk now, so a later click renders it with no wait. */
  preload: () => void;
};

/**
 * `React.lazy` for a route. A chunk the deploy removed does not throw to the
 * app's error boundary (a full-screen 500) and does not reload the tab: the
 * route renders `StalePage`, and useDeployWatcher raises its toast. Any other
 * failure still throws, and a later render or preload tries again.
 */
// biome-ignore lint/suspicious/noExplicitAny: matches React.lazy's own constraint
export function lazyPage<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyPage<T> {
  let loading: Promise<{ default: T }> | undefined;
  const load = () => {
    loading ??= factory().catch((error: unknown) => {
      if (!isChunkLoadError(error)) {
        loading = undefined;
        throw error;
      }
      return { default: StalePage as unknown as T };
    });
    return loading;
  };
  return Object.assign(lazy(load), {
    preload: () => {
      load().catch(() => {});
    },
  });
}
